/* eslint-disable no-nested-ternary */
import { forwardRef, RefObject, useEffect, useState } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  getError,
  DeductionsConfigurations as ExternalDeductionsConfigurations,
  CrossOutline,
  CheckOutline,
  DropdownInput,
  DatePickerInput,
  useToast,
  DEFAULT_DATE_FORMAT,
} from '@aslanpay/portal-common-lib';

import CurrencyInput, { formatValue } from 'react-currency-input-field';

import ProfileField from 'app/components/UI/ProfileField';
import ProfileSection from 'app/components/UI/ProfileSection';
import DropdownMultiSelectInput from 'app/components/UI/Inputs/DropdownMultiSelectInput';
import PayCalendarGpeFields from 'app/components/PayCalendars/PayCalendarProfile/PayCalendarGpeFields';

import useUpdatePayCalendar from 'app/hooks/useUpdatePayCalendar';

import {
  getGpeMaxDaysBeforePayday,
  getStartingBalanceDaysOption,
  WHOLE_PERIOD_STARTING_BALANCE_DAYS,
} from 'app/utils/payCalendars';
import { createPayCalendarProfileFormSchema } from 'app/utils/schemas/payCalendarProfile';
import { parseCurrencyIntoFloat } from 'app/utils/strings';
// import { getFirstPayDay } from 'app/utils/date'; // coming back in for 90 day fix
import { validateFutureDate } from 'app/utils/validations';
import { format } from 'date-fns';

import {
  PayCalendarLocation,
  PayCalendarWorkingDay,
  PayFrequency,
  PayrollCutoff,
  allowedPresetValues,
  AccrualPeriodType,
  DeductionsConfigurations,
} from 'app/constants/payCalendar';

import {
  getPaydayOptions,
  getCutOffOptions,
  getPayTypeOptions,
  getWorkingDaysOptions,
  getStartingBalanceDaysOptions,
  getOdpAllowancePercentagePercentageOptions,
  getGlobalAllowancePercentageOptions,
  getNonBusinessDayFailoverOptions,
} from '../../options';

import { PayCalendarProfileFormTabProps, PayCalendarProfileFormFields } from './types';

const PayCalendarProfileFormTab = forwardRef<HTMLDivElement, PayCalendarProfileFormTabProps>(
  ({ payCalendar, onCloseEditMode, companyConfiguration, section }, ref) => {
    const isReserveEnabled = companyConfiguration?.modulesConfig.modules.RESERVE === 'ENABLED';
    const isOnDemandPayEnabled =
      companyConfiguration?.modulesConfig.modules.ON_DEMAND_PAY === 'ENABLED';
    const queryClient = useQueryClient();

    const toast = useToast();

    const {
      register,
      control,
      handleSubmit,
      watch,
      reset,
      setFocus,
      setValue,
      trigger,
      formState: { errors, isValid, isDirty },
    } = useForm<PayCalendarProfileFormFields>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      shouldUnregister: false,
      defaultValues: {
        ...(payCalendar as PayCalendarProfileFormFields),
        // Handle custom odp allowance percentage values
        odpAllowancePercentage: (() => {
          const currentValue = payCalendar?.odpAllowancePercentage;
          if (!currentValue) return currentValue;

          // Check if the current value is a preset value (5, 10, 15, 20, 25, 30, 200)
          const presetValues = [5, 10, 15, 20, 25, 30, 200];
          if (presetValues.includes(currentValue)) {
            return currentValue;
          }

          // If it's not a preset value, it's a custom percentage
          // Set the dropdown to "Custom %" (200) and store the actual value in customOdpAllowancePercentage
          return 200;
        })(),
        customOdpAllowancePercentage: (() => {
          const currentValue = payCalendar?.odpAllowancePercentage;
          if (!currentValue) return undefined;

          // Check if the current value is a preset value
          const presetValues = [5, 10, 15, 20, 25, 30, 200];
          if (presetValues.includes(currentValue)) {
            return undefined; // Not a custom value
          }

          // If it's not a preset value, it's a custom percentage
          return currentValue;
        })(),
        // Handle custom global allowance percentage values
        globalAllowancePercentage: (() => {
          const currentValue = payCalendar?.globalAllowancePercentage;
          if (!currentValue && currentValue !== 0) return undefined;

          // Check if the current value is a preset value (5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 200)
          const presetValues = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 200];
          if (presetValues.includes(currentValue)) {
            return currentValue;
          }

          // If it's not a preset value, it's a custom percentage
          // Set the dropdown to "Custom %" (200) and store the actual value in customGlobalAllowancePercentage
          return 200;
        })(),
        customGlobalAllowancePercentage: (() => {
          const currentValue = payCalendar?.globalAllowancePercentage;
          if (!currentValue && currentValue !== 0) return undefined;

          // Check if the current value is a preset value
          const presetValues = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 200];
          if (presetValues.includes(currentValue)) {
            return undefined; // Not a custom value
          }

          // If it's not a preset value, it's a custom percentage
          return currentValue;
        })(),
        // Whole period has to be the default (not set after load) so that choosing 0 counts as a change
        startingBalanceDays: getStartingBalanceDaysOption(payCalendar),
        defaultAnnualGrossSalary: formatValue({
          value: (payCalendar as PayCalendarProfileFormFields).defaultAnnualGrossSalary?.toString(),
          prefix: '\u00A3',
          groupSeparator: ',',
          decimalSeparator: '.',
          decimalScale: 2,
        }),
      },
      resolver: yupResolver(createPayCalendarProfileFormSchema(payCalendar, companyConfiguration)),
    });

    // setup watchers
    const watchName = watch('name');
    const watchPayFrequency = watch('payFrequency');
    const watchPayday = watch('payday');
    const watchPaydayCustomDay = watch('paydayCustomDay');
    const watchPayrollCutoff = watch('payrollCutoff');
    const watchPayrollCutoffDays = watch('payrollCutoffDays');
    const watchOdpAllowancePercentage = watch('odpAllowancePercentage');
    const watchCustomOdpAllowancePercentage = watch('customOdpAllowancePercentage');
    const watchGlobalAllowancePercentage = watch('globalAllowancePercentage');
    const watchCustomGlobalAllowancePercentage = watch('customGlobalAllowancePercentage');
    const watchPayrollCutoffCustomDay = watch('payrollCutoffCustomDay');

    // setup option drop downs
    const paydayOptions = getPaydayOptions(watchPayFrequency);
    const cutoffOptions = getCutOffOptions(watchPayFrequency);
    const payTypeOptions = getPayTypeOptions();
    const workingDaysOptions = getWorkingDaysOptions();
    const odpAllowancePercentageOptions = getOdpAllowancePercentagePercentageOptions();
    const globalAllowancePercentageOptions = getGlobalAllowancePercentageOptions();
    const startingBalanceDaysOptions = getStartingBalanceDaysOptions(watchPayFrequency);
    const paydayNonBusinessDayFailoverOptions = getNonBusinessDayFailoverOptions();
    const cutOffNonBusinessDayFailoverOptions = getNonBusinessDayFailoverOptions();

    const gpeMaxDaysBeforePayday = getGpeMaxDaysBeforePayday(payCalendar?.payCycles);

    useEffect(() => {
      if (
        payCalendar?.odpAllowancePercentage !== undefined &&
        !allowedPresetValues.includes(Number(payCalendar?.odpAllowancePercentage))
      ) {
        setValue('odpAllowancePercentage', 200);
      }
    }, [payCalendar?.odpAllowancePercentage, setValue]);

    useEffect(() => {
      if (
        payCalendar?.globalAllowancePercentage !== undefined &&
        ![5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 200].includes(
          Number(payCalendar?.globalAllowancePercentage)
        )
      ) {
        setValue('globalAllowancePercentage', 200);
      }
    }, [payCalendar?.globalAllowancePercentage, setValue]);

    const [gpeServerError, setGpeServerError] = useState<string>();

    const { mutate: updatePayCalendar, isLoading: isUpdatingPayCalendar } = useUpdatePayCalendar({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Pay calendar details saved',
          description: 'The pay calendar details were successfully saved!',
        });

        queryClient.invalidateQueries({ queryKey: ['fetchPayCalendar', payCalendar?.id] });

        onCloseEditMode();
      },
      onError: (error) => {
        const baseError = getError(error);

        if (baseError.code?.startsWith('GPE')) {
          // Surface Get Paid Early rejections (e.g. GPE_REQUIRES_PAYDAY_MAT, GPE_DAYS_INVALID) inline.
          setGpeServerError(baseError.message);
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Get Paid Early configuration rejected',
            description: baseError.message,
          });
        } else if (baseError.codeDescription === 'CONFLICT') {
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Pay calendar already exists',
            description:
              'There is already a pay calendar with the inserted name. Please, submit the form with a unique pay calendar name.',
          });
        } else {
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Something went wrong',
            description: `It was not possible to save the pay calendar details. The error given was ${baseError.message}`,
          });
        }
      },
    });

    const handleCancelUpdate = () => {
      reset({
        name: payCalendar?.name,
        externalPayCalendarId: payCalendar?.externalPayCalendarId,
        payFrequency: payCalendar?.payFrequency,
        payType: payCalendar?.payType,
        payday: payCalendar?.payday,
        paydayLastCustomWeekday: payCalendar?.paydayLastCustomWeekday,
        paydayCustomDay: payCalendar?.paydayCustomDay,
        workingDays: payCalendar?.workingDays,
        location: payCalendar?.location,
        payrollCutoff: payCalendar?.payrollCutoff,
        payrollCutoffDays: payCalendar?.payrollCutoffDays,
        payrollCutoffCustomDay: payCalendar?.payrollCutoffCustomDay,
        odpAllowancePercentage: undefined,
        globalAllowancePercentage: undefined,
        startingBalanceDays: getStartingBalanceDaysOption(payCalendar),
        payrollCutoffFixedDay: undefined,
        paydayFixedDay: undefined,
        paydayNonBusinessDayFailover: undefined,
        cutoffNonBusinessDayFailover: undefined,
        customOdpAllowancePercentage: undefined,
        customGlobalAllowancePercentage: undefined,
        fullPeriodStartingBalance: undefined,
        salariedDeductionsRequestMadeAvailableConfig:
          payCalendar?.salariedDeductionsRequestMadeAvailableConfig as ExternalDeductionsConfigurations,
        nonSalariedDeductionsRequestMadeAvailableConfig:
          payCalendar?.nonSalariedDeductionsRequestMadeAvailableConfig as ExternalDeductionsConfigurations,
        salariedGpeEnabled: payCalendar?.salariedGpeEnabled ?? false,
        salariedGpeDaysBeforePaydayCap: payCalendar?.salariedGpeDaysBeforePaydayCap ?? undefined,
        nonSalariedGpeEnabled: payCalendar?.nonSalariedGpeEnabled ?? false,
        nonSalariedGpeDaysBeforePaydayCap:
          payCalendar?.nonSalariedGpeDaysBeforePaydayCap ?? undefined,
      });

      setGpeServerError(undefined);

      onCloseEditMode();
    };

    // this code is coming back in for the 90-day fix

    // const firstPayDay: Date = getFirstPayDay(
    //   new Date(startDate ?? '1970-01-01'),
    //   watchPayFrequency,
    //   payCalendar?.paydayNonBusinessDayFailover ?? 'NO_CHANGE',
    //   []
    // );

    const handleUpdatePayCalendar = (data: PayCalendarProfileFormFields) => {
      setGpeServerError(undefined);

      const defaultAnnualGrossSalaryParsed = parseCurrencyIntoFloat(
        data.defaultAnnualGrossSalary?.toString() || ''
      );

      // now need to reverse map any custom day values to the settings

      // maps to: N_DAY_OF_PERIOD, N_BUSINESS_DAY_OF_PERIOD, CUSTOM_DAY, CUSTOM_DAY_IN_ARREARS
      let paydayCustomDayValue;

      switch (data.payday) {
        case 'N_DAY_OF_PERIOD':
          paydayCustomDayValue = data.paydayCustomDay;
          break;
        case 'N_BUSINESS_DAY_OF_PERIOD':
          paydayCustomDayValue = data.paydayCustomDay;
          break;
        default:
          paydayCustomDayValue = data.paydayCustomDay ?? undefined;
      }

      let cutOffValue;

      switch (data.payrollCutoff) {
        case 'N_DAYS_BEFORE_PAYDAY':
          cutOffValue = data.payrollCutoffDays;
          break;
        case 'N_BUSINESS_DAYS_BEFORE_PAYDAY':
          cutOffValue = data.payrollCutoffDays;
          break;
        case 'N_DAY_OF_PERIOD':
          cutOffValue = data.payrollCutoffDays;
          break;
        case 'N_BUSINESS_DAY_OF_PERIOD':
          cutOffValue = data.payrollCutoffDays;
          break;
        default:
          cutOffValue = undefined;
      }

      const isWholePeriod = data.startingBalanceDays === WHOLE_PERIOD_STARTING_BALANCE_DAYS;
      const startingDays = isWholePeriod ? 0 : data.startingBalanceDays;

      updatePayCalendar({
        id: payCalendar?.id || '',
        name: data.name,
        externalPayCalendarId: data.externalPayCalendarId?.trim() || undefined,
        location: data.location,
        currency: data.currency,
        payFrequency: data.payFrequency,
        // if custom allowance is set, we need to set it here instead of odp allowance percentage
        odpAllowancePercentage:
          data.customOdpAllowancePercentage !== undefined
            ? data.customOdpAllowancePercentage
            : data.odpAllowancePercentage,
        // if custom global allowance is set, we need to set it here instead of global allowance percentage
        globalAllowancePercentage:
          data.customGlobalAllowancePercentage !== undefined
            ? data.customGlobalAllowancePercentage
            : data.globalAllowancePercentage,
        startingBalanceDays: startingDays,
        defaultAnnualGrossSalary: Number.isNaN(defaultAnnualGrossSalaryParsed)
          ? undefined
          : defaultAnnualGrossSalaryParsed,
        startDate: data.startDate,
        payrollCutoff: data.payrollCutoff,
        payrollCutoffDays: cutOffValue,
        payrollCutoffCustomDay:
          data.payrollCutoff === 'CUSTOM_DAY' ||
          data.payrollCutoff === 'CUSTOM_DAY_IN_ARREARS' ||
          data.payrollCutoff === 'N_DAYS_BEFORE_PAYDAY' ||
          data.payrollCutoff === 'N_BUSINESS_DAYS_BEFORE_PAYDAY' ||
          data.payrollCutoff === 'N_DAY_OF_PERIOD' ||
          data.payrollCutoff === 'N_BUSINESS_DAY_OF_PERIOD'
            ? data.payrollCutoffCustomDay
            : undefined,
        payday: data.payday,
        paydayLastCustomWeekday:
          data.payday === 'LAST_CUSTOM_WEEKDAY' ? data.paydayLastCustomWeekday : undefined,
        ...(paydayCustomDayValue !== undefined && { paydayCustomDay: paydayCustomDayValue }),
        workingDays: data.workingDays,
        payType: data.payType,
        ...(data.payrollCutoffFixedDay && { payrollCutoffFixedDay: data.payrollCutoffFixedDay }),
        ...(data.paydayFixedDay && { paydayFixedDay: data.paydayFixedDay }),
        paydayNonBusinessDayFailover: data.paydayNonBusinessDayFailover,
        cutoffNonBusinessDayFailover: data.cutoffNonBusinessDayFailover,
        fullPeriodStartingBalance: isWholePeriod,
        ...(data.paydayFirstCustomWeekday && {
          paydayFirstCustomWeekday: data.paydayFirstCustomWeekday,
        }),
        accrualPeriod: data.accrualPeriod,
        salariedDeductionsRequestMadeAvailableConfig:
          (data.salariedDeductionsRequestMadeAvailableConfig ||
            'DEDUCTIONS_PAY_CYCLE_PAYDAY') as ExternalDeductionsConfigurations,
        nonSalariedDeductionsRequestMadeAvailableConfig:
          (data.nonSalariedDeductionsRequestMadeAvailableConfig ||
            'DEDUCTIONS_PAY_CYCLE_PAYDAY') as ExternalDeductionsConfigurations,
        salariedGpeEnabled: data.salariedGpeEnabled ?? false,
        salariedGpeDaysBeforePaydayCap: data.salariedGpeDaysBeforePaydayCap ?? null,
        nonSalariedGpeEnabled: data.nonSalariedGpeEnabled ?? false,
        nonSalariedGpeDaysBeforePaydayCap: data.nonSalariedGpeDaysBeforePaydayCap ?? null,
      });
    };

    const resetAndFocusField = (field: keyof PayCalendarProfileFormFields) => {
      setValue(field, '', { shouldValidate: true, shouldDirty: true });
      setFocus(field);
    };

    return (
      <form
        style={{ flexGrow: 1 }}
        id="edit-pay-calendar-details"
        onSubmit={handleSubmit(handleUpdatePayCalendar)}
        noValidate
      >
        {/* Pay calendar name */}

        <VStack spacing="40px" align="stretch" w="full">
          {section === 'general' && (
            <>
              <VStack spacing="40px" align="stretch" w="full">
                {/* General details has id, name and frequency */}

                <ProfileSection title="General details" isEditMode maxW="full">
                  <ProfileField label="Pay calendar ID" value={payCalendar?.friendlyId} />
                  <ProfileField
                    label="Pay calendar internal ID"
                    value={payCalendar?.id}
                    copyToClipboard
                  />
                  <FormControl isInvalid={!!errors?.name?.message} isRequired>
                    <FormLabel>Pay calendar name </FormLabel>
                    <InputGroup>
                      <Input type="text" placeholder="E.g. “Monthly”" {...register('name')} />
                      {watchName && (
                        <InputRightElement>
                          <IconButton
                            aria-label="Clear name"
                            icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                            variant="link"
                            onClick={() => resetAndFocusField('name')}
                          />
                        </InputRightElement>
                      )}
                    </InputGroup>
                    <FormErrorMessage>{errors?.name?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors?.externalPayCalendarId?.message}>
                    <FormLabel>External pay calendar ID</FormLabel>
                    <Input
                      type="text"
                      placeholder="Optional — matching pay calendar id in the payroll provider (e.g. Workday)"
                      {...register('externalPayCalendarId')}
                    />
                    <FormErrorMessage>{errors?.externalPayCalendarId?.message}</FormErrorMessage>
                  </FormControl>

                  {/* pay frequency, this impacts the selections for paydy and cutoff day - monthly all the options; weekly, fortnigtly and 4-weekly a subset */}

                  <HStack spacing="20px">
                    <ProfileField
                      label="Pay frequency"
                      value={PayFrequency[payCalendar?.payFrequency || 'MONTHLY']}
                    />
                  </HStack>
                </ProfileSection>

                {/* Payday information has how, start, when, number */}

                <ProfileSection title="Payday information" isEditMode maxW="full">
                  {/* in period or in-arrears */}

                  {payCalendar?.hasEmployeeWithSalary ? (
                    <ProfileField
                      label="How do you pay"
                      value={
                        payTypeOptions.find((payType) => payType.value === payCalendar.payType)
                          ?.text
                      }
                    />
                  ) : (
                    <FormControl isInvalid={!!errors?.payType?.message} isRequired>
                      <FormLabel>How do you pay</FormLabel>
                      <Controller
                        control={control}
                        name="payType"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DropdownInput
                            placeholder="Select a type of payment"
                            defaultValue={payTypeOptions.find((type) => type.value === value)}
                            options={payTypeOptions}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                        )}
                      />
                      <FormErrorMessage>{errors?.payType?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                  <HStack spacing="20px">
                    <ProfileField
                      label="When does the pay calendar start"
                      value={format(
                        new Date(payCalendar?.startDate ?? '1970-01-01'),
                        DEFAULT_DATE_FORMAT
                      )}
                    />
                  </HStack>
                  {/* pay day - has extra options when n day of period, n business day of period, cutom weekday, fixed day are selected */}
                  <FormControl isInvalid={!!errors?.payday?.message} isRequired>
                    <FormLabel>Pay day</FormLabel>
                    <Controller
                      control={control}
                      name="payday"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          placeholder="When do you pay your employees?"
                          defaultValue={paydayOptions.find((payday) => payday.value === value)}
                          options={paydayOptions}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    <FormErrorMessage>{errors?.payday?.message}</FormErrorMessage>
                  </FormControl>
                  {/* this is the last custom weekday and the user can select a weekday from the list */}
                  {watchPayday === 'LAST_CUSTOM_WEEKDAY' && (
                    <FormControl isInvalid={!!errors?.paydayLastCustomWeekday?.message} isRequired>
                      <FormLabel>Last week day</FormLabel>
                      <Controller
                        control={control}
                        name="paydayLastCustomWeekday"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DropdownInput
                            placeholder="Select the last week day"
                            defaultValue={workingDaysOptions?.find((day) => day.value === value)}
                            options={workingDaysOptions}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                        )}
                      />
                      <FormErrorMessage>
                        {errors?.paydayLastCustomWeekday?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                  {/* this is the last custom weekday and the user can select a weekday from the list */}
                  {watchPayday === 'N_DAY_OF_PERIOD' && (
                    <FormControl isInvalid={!!errors?.paydayCustomDay?.message} isRequired>
                      <FormLabel>Nth day of the period</FormLabel>
                      <InputGroup>
                        <Input
                          type="number"
                          placeholder="How many days?"
                          {...register('paydayCustomDay')}
                        />
                        {watchPaydayCustomDay && (
                          <InputRightElement>
                            <IconButton
                              aria-label="Clear payday custom day"
                              icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                              variant="link"
                              onClick={() => resetAndFocusField('paydayCustomDay')}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <FormErrorMessage>{errors?.paydayCustomDay?.message}</FormErrorMessage>
                    </FormControl>
                  )}

                  {/* nth business dy fo the period */}
                  {watchPayday === 'N_BUSINESS_DAY_OF_PERIOD' && (
                    <FormControl isInvalid={!!errors?.paydayCustomDay?.message} isRequired>
                      <FormLabel>Nth business day of the period</FormLabel>
                      <InputGroup>
                        <Input
                          type="number"
                          placeholder="How many business days?"
                          {...register('paydayCustomDay')}
                        />
                        {watchPaydayCustomDay && (
                          <InputRightElement>
                            <IconButton
                              aria-label="Clear payday custom day"
                              icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                              variant="link"
                              onClick={() => resetAndFocusField('paydayCustomDay')}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <FormErrorMessage>{errors?.paydayCustomDay?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                  {/* this is the BACKWARDS compatibility we don't use these anymore */}
                  {(watchPayday === 'CUSTOM_DAY_IN_ARREARS' || watchPayday === 'CUSTOM_DAY') && (
                    <FormControl isInvalid={!!errors?.paydayCustomDay?.message} isRequired>
                      <FormLabel>Custom day</FormLabel>
                      <InputGroup>
                        <Input
                          type="number"
                          placeholder="Enter the day of the month"
                          {...register('paydayCustomDay')}
                        />
                        {watchPaydayCustomDay && (
                          <InputRightElement>
                            <IconButton
                              aria-label="Clear payday custom day"
                              icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                              variant="link"
                              onClick={() => resetAndFocusField('paydayCustomDay')}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <FormErrorMessage>{errors?.paydayCustomDay?.message}</FormErrorMessage>
                    </FormControl>
                  )}

                  {watchPayday !== undefined && watchPayday === 'FIXED_DAY' && (
                    <FormControl isInvalid={!!errors?.paydayFixedDay?.message} isRequired>
                      <FormLabel>Fixed day</FormLabel>
                      <Controller
                        control={control}
                        name="paydayFixedDay"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DatePickerInput
                            placeholder="What fixed day do you use?"
                            defaultValue={value}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                        )}
                      />
                      <FormErrorMessage>{errors?.paydayFixedDay?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                </ProfileSection>
              </VStack>
              {/* Calendar defaults has working days, default payday action, location, default salary */}
              <VStack spacing="40px" align="stretch" w="full">
                <ProfileSection title="Calendar defaults" isEditMode maxW="full">
                  {payCalendar?.hasEmployeeWithSalary ||
                  (payCalendar && !validateFutureDate(payCalendar?.startDate)) ? (
                    <ProfileField
                      label="Working days"
                      value={payCalendar?.workingDays
                        .map((day) => PayCalendarWorkingDay[day])
                        .join(', ')}
                    />
                  ) : (
                    <FormControl isInvalid={!!errors?.workingDays?.message} isRequired>
                      <FormLabel>Working days</FormLabel>
                      <Controller
                        control={control}
                        name="workingDays"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DropdownMultiSelectInput
                            placeholder="Select one or more working days"
                            defaultValue={value}
                            options={workingDaysOptions}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                        )}
                      />
                      <FormErrorMessage>{errors?.workingDays?.message}</FormErrorMessage>
                    </FormControl>
                  )}

                  {payCalendar?.hasEmployeeWithSalary ? (
                    <ProfileField
                      label="Default payday action"
                      value={
                        paydayNonBusinessDayFailoverOptions.find(
                          (paydayNonBusinessDayFailover) =>
                            paydayNonBusinessDayFailover.value ===
                            payCalendar.paydayNonBusinessDayFailover
                        )?.text
                      }
                    />
                  ) : (
                    <FormControl
                      isInvalid={!!errors?.paydayNonBusinessDayFailover?.message}
                      isRequired
                    >
                      <FormLabel>Default payday action</FormLabel>
                      <Controller
                        control={control}
                        name="paydayNonBusinessDayFailover"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DropdownInput
                            placeholder="If payday lands on a weekend or bank holiday"
                            defaultValue={paydayNonBusinessDayFailoverOptions.find(
                              (paydayNonBusinessDayFailover) =>
                                paydayNonBusinessDayFailover.value === value
                            )}
                            options={paydayNonBusinessDayFailoverOptions}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                        )}
                      />
                      <FormErrorMessage>
                        {errors?.paydayNonBusinessDayFailover?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                  {payCalendar?.hasEmployeeWithSalary ? (
                    <ProfileField
                      label="Location (country)"
                      value={PayCalendarLocation[payCalendar?.location || 'ENGLAND_WALES']}
                    />
                  ) : (
                    <ProfileField
                      label="Location (country)"
                      value={PayCalendarLocation[payCalendar?.location || 'ENGLAND_WALES']}
                    />
                    // <FormControl isInvalid={!!errors?.location?.message} isRequired>
                    //   <FormLabel>Location (country)</FormLabel>
                    //   <Controller
                    //     control={control}
                    //     name="location"
                    //     render={({ field: { onChange, onBlur, value } }) => (
                    //       <DropdownInput
                    //         placeholder="In which country are the employees based?"
                    //         defaultValue={locationOptions.find((location) => location.value === value)}
                    //         options={locationOptions}
                    //         onChange={onChange}
                    //         onBlur={onBlur}
                    //       />
                    //     )}
                    //   />
                    //   <FormErrorMessage>{errors?.location?.message}</FormErrorMessage>
                    // </FormControl>
                  )}
                  <FormControl isInvalid={!!errors?.defaultAnnualGrossSalary?.message}>
                    <FormLabel>Default salary amount (optional)</FormLabel>
                    <InputGroup>
                      <Input
                        defaultValue={payCalendar?.defaultAnnualGrossSalary}
                        as={CurrencyInput}
                        prefix={'\u00A3'}
                        decimalSeparator="."
                        groupSeparator=","
                        decimalScale={2}
                        type="text"
                        placeholder={'Default salary for created employees. E.g. \u00A324,000.00'}
                        {...register('defaultAnnualGrossSalary')}
                      />
                    </InputGroup>
                    <FormErrorMessage>{errors?.defaultAnnualGrossSalary?.message}</FormErrorMessage>
                  </FormControl>
                </ProfileSection>

                {/* Cut off details has cut off type, number, payday action */}

                <ProfileSection
                  title="Cut-off details"
                  isEditMode
                  maxW="full"
                  tooltip="It’s a date and time programmed to ensure that the employer's payroll team can accurately process pay during this period."
                >
                  {payCalendar?.hasEmployeeWithSalary ? (
                    <ProfileField
                      label="Cut-off type"
                      value={PayrollCutoff[payCalendar?.payrollCutoff || 'N_DAY_OF_PERIOD']}
                    />
                  ) : (
                    <FormControl isInvalid={!!errors?.payrollCutoff?.message} isRequired>
                      <FormLabel>Cut-off type</FormLabel>
                      <Controller
                        control={control}
                        name="payrollCutoff"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DropdownInput
                            placeholder="Select a cut-off type"
                            defaultValue={cutoffOptions.find((type) => type.value === value)}
                            options={cutoffOptions}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                        )}
                      />
                      <FormErrorMessage>{errors?.payrollCutoff?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                  {watchPayrollCutoff === 'N_DAYS_BEFORE_PAYDAY' ||
                  watchPayrollCutoff === 'N_BUSINESS_DAYS_BEFORE_PAYDAY' ||
                  watchPayrollCutoff === 'N_DAY_OF_PERIOD' ||
                  watchPayrollCutoff === 'N_BUSINESS_DAY_OF_PERIOD' ? (
                    <FormControl isInvalid={!!errors?.payrollCutoffDays?.message} isRequired>
                      <FormLabel>Cut-off number of days</FormLabel>
                      <InputGroup>
                        <Input
                          type="number"
                          defaultValue={payCalendar?.payrollCutoffDays}
                          placeholder={
                            watchPayrollCutoff === 'N_DAY_OF_PERIOD'
                              ? 'Nth day of period'
                              : watchPayrollCutoff === 'N_BUSINESS_DAY_OF_PERIOD'
                              ? 'Nth business day of period'
                              : watchPayrollCutoff === 'N_BUSINESS_DAYS_BEFORE_PAYDAY'
                              ? 'Number of business days before payday'
                              : 'Number of days before payday'
                          }
                          {...register('payrollCutoffDays')}
                        />
                        {watchPayrollCutoffDays && (
                          <InputRightElement>
                            <IconButton
                              aria-label="Clear payroll cut-off days"
                              icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                              variant="link"
                              onClick={() => resetAndFocusField('payrollCutoffDays')}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <FormErrorMessage>{errors?.payrollCutoffDays?.message}</FormErrorMessage>
                    </FormControl>
                  ) : watchPayrollCutoff === 'CUSTOM_DAY' ||
                    watchPayrollCutoff === 'CUSTOM_DAY_IN_ARREARS' ? (
                    <FormControl isInvalid={!!errors?.payrollCutoffCustomDay?.message} isRequired>
                      <FormLabel>Cut-off custom day</FormLabel>
                      <InputGroup>
                        <Input
                          type="number"
                          defaultValue={payCalendar?.payrollCutoffCustomDay}
                          placeholder="Custom day value"
                          {...register('payrollCutoffCustomDay')}
                        />
                        {watchPayrollCutoffCustomDay && (
                          <InputRightElement>
                            <IconButton
                              aria-label="Clear payroll cut-off days"
                              icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                              variant="link"
                              onClick={() => resetAndFocusField('payrollCutoffCustomDay')}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <FormErrorMessage>{errors?.payrollCutoffCustomDay?.message}</FormErrorMessage>
                    </FormControl>
                  ) : (
                    <>
                      <FormControl isInvalid={!!errors?.payrollCutoffFixedDay?.message} isRequired>
                        <FormLabel>Fixed day</FormLabel>
                        <Controller
                          control={control}
                          name="payrollCutoffFixedDay"
                          render={({ field: { onChange, onBlur, value } }) => (
                            <DatePickerInput
                              placeholder="What fixed day do you use?"
                              defaultValue={value}
                              onChange={onChange}
                              onBlur={onBlur}
                            />
                          )}
                        />
                        <FormErrorMessage>
                          {errors?.payrollCutoffFixedDay?.message}
                        </FormErrorMessage>
                      </FormControl>
                      {/* this comment is behaving weirdly if removed */}
                    </>
                  )}

                  {payCalendar?.hasEmployeeWithSalary ? (
                    <ProfileField
                      label="Default cut-off action"
                      value={
                        paydayNonBusinessDayFailoverOptions.find(
                          (cutoffNonBusinessDayFailover) =>
                            cutoffNonBusinessDayFailover.value ===
                            payCalendar.cutoffNonBusinessDayFailover
                        )?.text
                      }
                    />
                  ) : (
                    <FormControl
                      isInvalid={!!errors?.cutoffNonBusinessDayFailover?.message}
                      isRequired
                    >
                      <FormLabel>Default cut-off action</FormLabel>
                      <Controller
                        control={control}
                        name="cutoffNonBusinessDayFailover"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DropdownInput
                            placeholder="If payday lands on a weekend or bank holiday"
                            defaultValue={cutOffNonBusinessDayFailoverOptions.find(
                              (cutoffNonBusinessDayFailover) =>
                                cutoffNonBusinessDayFailover.value === value
                            )}
                            options={cutOffNonBusinessDayFailoverOptions}
                            onChange={onChange}
                            onBlur={onBlur}
                          />
                        )}
                      />
                      <FormErrorMessage>
                        {errors?.paydayNonBusinessDayFailover?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </ProfileSection>
              </VStack>
            </>
          )}
          {section === 'configuration' && (
            <>
              <ProfileSection title="Dynamic Allowances" isEditMode maxW="full">
                {isReserveEnabled && (
                  <FormControl isInvalid={!!errors?.globalAllowancePercentage?.message} isRequired>
                    <FormLabel>Global limit (%)</FormLabel>
                    <Controller
                      control={control}
                      name="globalAllowancePercentage"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          placeholder="Users can set and spend up to this limit"
                          defaultValue={(() => {
                            // If the current value is a preset value, find it in the options
                            const presetOption = globalAllowancePercentageOptions.find(
                              (option) => option.value === value
                            );
                            if (presetOption) return presetOption;

                            // If the current value is not a preset value, it's a custom percentage
                            // Show "Custom %" as selected
                            return globalAllowancePercentageOptions.find(
                              (option) => option.value === 200
                            );
                          })()}
                          options={globalAllowancePercentageOptions}
                          onChange={(changedValue) => {
                            onChange(changedValue);
                            trigger('odpAllowancePercentage');
                            trigger('customOdpAllowancePercentage');
                          }}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    <FormErrorMessage>
                      {errors?.globalAllowancePercentage?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}

                {isReserveEnabled && watchGlobalAllowancePercentage === 200 && (
                  <FormControl
                    isInvalid={!!errors?.customGlobalAllowancePercentage?.message}
                    isRequired
                  >
                    <FormLabel>What custom percentage would you like?</FormLabel>
                    <InputGroup>
                      <Input
                        type="number"
                        defaultValue={payCalendar?.globalAllowancePercentage}
                        placeholder="Enter percentage from 0 to 100"
                        {...register('customGlobalAllowancePercentage')}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label="Clear custom percentage"
                          icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                          variant="link"
                          onClick={() => resetAndFocusField('customGlobalAllowancePercentage')}
                        />
                      </InputRightElement>
                    </InputGroup>

                    <FormErrorMessage>
                      {errors?.customGlobalAllowancePercentage?.message}
                    </FormErrorMessage>

                    {/** Warning Message for High Percentage */}
                    {watchCustomGlobalAllowancePercentage !== undefined &&
                      watchCustomGlobalAllowancePercentage > 50 && (
                        <Text color="red" size="8" fontSize="sm" mt="2">
                          <strong>Warning:</strong> This is a high percentage, please be sure.
                        </Text>
                      )}
                    {/** Warning Message for Zero Percentage */}
                    {watchCustomGlobalAllowancePercentage !== undefined &&
                      String(watchCustomGlobalAllowancePercentage).trim() !== '' &&
                      watchCustomGlobalAllowancePercentage === 0 && (
                        <Text color="red" size="8" fontSize="sm" mt="2">
                          <strong>Warning:</strong> This will disable the global limit feature.
                        </Text>
                      )}
                  </FormControl>
                )}

                {isOnDemandPayEnabled && (
                  <FormControl isInvalid={!!errors?.odpAllowancePercentage?.message} isRequired>
                    <FormLabel>On-demand pay sub-limit (%)</FormLabel>
                    <Controller
                      control={control}
                      name="odpAllowancePercentage"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          placeholder="Portion that an employee can accrue on a daily basis"
                          defaultValue={(() => {
                            // If the current value is a preset value, find it in the options
                            const presetOption = odpAllowancePercentageOptions.find(
                              (option) => option.value === value
                            );
                            if (presetOption) return presetOption;

                            // If the current value is not a preset value, it's a custom percentage
                            // Show "Custom %" as selected
                            return odpAllowancePercentageOptions.find(
                              (option) => option.value === 200
                            );
                          })()}
                          options={odpAllowancePercentageOptions}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    <FormErrorMessage>{errors?.odpAllowancePercentage?.message}</FormErrorMessage>
                  </FormControl>
                )}

                {isOnDemandPayEnabled && watchOdpAllowancePercentage === 200 && (
                  <FormControl
                    isInvalid={!!errors?.customOdpAllowancePercentage?.message}
                    isRequired
                  >
                    <FormLabel>What custom percentage would you like?</FormLabel>
                    <InputGroup>
                      <Input
                        type="number"
                        defaultValue={payCalendar?.odpAllowancePercentage}
                        placeholder="Enter percentage from 0 to 100"
                        {...register('customOdpAllowancePercentage')}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label="Clear custom percentage"
                          icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                          variant="link"
                          onClick={() => resetAndFocusField('customOdpAllowancePercentage')}
                        />
                      </InputRightElement>
                    </InputGroup>

                    <FormErrorMessage>
                      {errors?.customOdpAllowancePercentage?.message}
                    </FormErrorMessage>

                    {/** Warning Message for High Percentage */}
                    {watchCustomOdpAllowancePercentage !== undefined &&
                      watchCustomOdpAllowancePercentage > 50 && (
                        <Text color="red" size="8" fontSize="sm" mt="2">
                          <strong>Warning:</strong> This is a high percentage, please be sure.
                        </Text>
                      )}
                    {/** Warning Message for Zero Percentage */}
                    {watchCustomOdpAllowancePercentage !== undefined &&
                      String(watchCustomOdpAllowancePercentage).trim() !== '' &&
                      Number(watchCustomOdpAllowancePercentage) === 0 && (
                        <Text color="red" size="8" fontSize="sm" mt="2">
                          <strong>Warning:</strong> This is a zero (0) percentage, please be sure.
                        </Text>
                      )}
                  </FormControl>
                )}
              </ProfileSection>

              <ProfileSection title="ODP Configuration" isEditMode maxW="full">
                <ProfileField
                  label="Accrual period"
                  value={(() => {
                    if (!payCalendar?.accrualPeriod) return '-';
                    return (
                      AccrualPeriodType[
                        payCalendar.accrualPeriod as keyof typeof AccrualPeriodType
                      ] || '-'
                    );
                  })()}
                />

                <FormControl isInvalid={!!errors?.startingBalanceDays?.message} isRequired>
                  <FormLabel>1st of the month accrual (working days)</FormLabel>
                  <Controller
                    control={control}
                    name="startingBalanceDays"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <DropdownInput
                        placeholder="Portion that an employee can accrue on a daily basis"
                        defaultValue={startingBalanceDaysOptions.find(
                          (option) => option.value === value
                        )}
                        options={startingBalanceDaysOptions}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <FormErrorMessage>{errors?.startingBalanceDays?.message}</FormErrorMessage>
                </FormControl>
              </ProfileSection>

              <ProfileSection title="Set and Spend Configuration" isEditMode maxW="full">
                <Text textStyle="h6SemiBold" color="brand.darkPurple">
                  Made Available Time (MAT)
                </Text>
                <ProfileField
                  label="Salaried workers"
                  value={(() => {
                    if (!payCalendar?.salariedDeductionsRequestMadeAvailableConfig) return '-';
                    return (
                      DeductionsConfigurations[
                        payCalendar.salariedDeductionsRequestMadeAvailableConfig as keyof typeof DeductionsConfigurations
                      ] || '-'
                    );
                  })()}
                />

                <ProfileField
                  label="Non-salaried workers"
                  value={(() => {
                    if (!payCalendar?.nonSalariedDeductionsRequestMadeAvailableConfig) return '-';
                    return (
                      DeductionsConfigurations[
                        payCalendar.nonSalariedDeductionsRequestMadeAvailableConfig as keyof typeof DeductionsConfigurations
                      ] || '-'
                    );
                  })()}
                />

                <PayCalendarGpeFields
                  control={control}
                  errors={errors}
                  salariedMatIsPayday={
                    payCalendar?.salariedDeductionsRequestMadeAvailableConfig ===
                    'DEDUCTIONS_PAY_CYCLE_PAYDAY'
                  }
                  nonSalariedMatIsPayday={
                    payCalendar?.nonSalariedDeductionsRequestMadeAvailableConfig ===
                    'DEDUCTIONS_PAY_CYCLE_PAYDAY'
                  }
                  maxDaysBeforePayday={gpeMaxDaysBeforePayday}
                  serverError={gpeServerError}
                />
              </ProfileSection>
            </>
          )}
        </VStack>
        <Portal containerRef={ref as RefObject<HTMLElement>}>
          <Flex gap="20px" alignItems="flex-start">
            <Button variant="outline" size="sm" onClick={handleCancelUpdate}>
              Cancel
            </Button>
            <Button
              leftIcon={<CheckOutline boxSize="20px" />}
              size="sm"
              type="submit"
              form="edit-pay-calendar-details"
              isLoading={isUpdatingPayCalendar}
              isDisabled={!isValid || !isDirty}
            >
              Save changes
            </Button>
          </Flex>
        </Portal>
      </form>
    );
  }
);

PayCalendarProfileFormTab.displayName = 'PayCalendarProfileFormTab';

export default PayCalendarProfileFormTab;
